import React, { useState, useEffect, useRef, useMemo, useCallback, useLayoutEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Typography, IconButton, useTheme, useMediaQuery,
  Grid, InputAdornment, Chip, Checkbox, Slide, List, ListItem,
  ListItemIcon, ListItemText, Paper, ClickAwayListener,
  GlobalStyles, TextField, Alert, Collapse,
} from '@mui/material';
import CloseIcon               from '@mui/icons-material/Close';
import FilterListIcon           from '@mui/icons-material/FilterList';
import SearchIcon               from '@mui/icons-material/Search';
import ClearIcon                from '@mui/icons-material/Clear';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon             from '@mui/icons-material/CheckBox';
import HistoryIcon              from '@mui/icons-material/History';
import AssignmentTurnedInIcon   from '@mui/icons-material/AssignmentTurnedIn';
import UpdateIcon               from '@mui/icons-material/Update';
import RestoreIcon              from '@mui/icons-material/Restore';
import { useDispatch, useSelector } from 'react-redux';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment }        from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker }           from '@mui/x-date-pickers/DatePicker';
import moment from 'moment';
import { GetDevelopersData }    from '../Redux/Actions/DevelopersAction';
import { GetAllCampainData }    from '../Redux/Actions/Campaign_Actions';
import { Get_All_Sales_Action } from '../Redux/Actions/SalesAction';
import { GetAllchannalData }    from '../Redux/Actions/ChannalActions';
import { GetallProjectsdata }   from '../Redux/Actions/Projects_Action';
import { GetAll_stage_Data }    from '../Redux/Actions/Stage_Action';

const SlideTransition = React.forwardRef((props, ref) => (
  <Slide direction="left" ref={ref} {...props} />
));

const icon        = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;
const EXCLUDE     = ['Default','Default M','Default T','Default S','Developer','Dev'];
const LAST_FILTER_KEY = 'quickFilterMarketer_lastUsed'; // ✅ key منفصل للـ Marketer

const stageColors = {
  Discovery: '#6366F1', Viewing: '#10B981', Offer: '#F59E0B',
  Urgent: '#EF4444', Lost: '#6B7280', Closing: '#0D47A1',
};

const EMPTY_FILTERS = {
  budget: '', stage: [], oldstage: [], sales: [], chanel: [],
  campaign: [], project: [], developer: [], assignedTo: [],
  createdAt: { from: null, to: null }, stagedateupdated: { from: null, to: null },
};

/* ── StableMenu ─────────────────────────────────────────────────── */
const StableMenu = ({
  open, options, selected, searchValue, onSearchChange,
  onToggle, onSelectAll, onClose,
  anchorPosition, mode, textSecondary, inputBg, bg, border, accent,
  getId, getName,
}) => {
  const listRef      = useRef(null);
  const scrollTopRef = useRef(0);
  const searchRef    = useRef(null);
  const textPrimary  = mode === 'dark' ? '#ffffff' : '#1a1a2e';

  useLayoutEffect(() => { if (listRef.current) listRef.current.scrollTop = scrollTopRef.current; });

  useEffect(() => {
    if (open && searchRef.current) {
      setTimeout(() => { const el = searchRef.current?.querySelector('input'); if (el) el.focus(); }, 50);
    }
  }, [open]);

  const filtered = options.filter(opt =>
    getName(opt).toLowerCase().includes(searchValue.toLowerCase()) ||
    selected.some(s => getId(s) === getId(opt))
  );

  if (!open) return null;

  return (
    <ClickAwayListener onClickAway={onClose}>
      <Paper sx={{ position:'fixed', top:anchorPosition.top, left:anchorPosition.left, width:anchorPosition.width, maxHeight:360, bgcolor:bg, border:`1px solid ${border}`, borderRadius:'12px', overflow:'hidden', zIndex:999999, boxShadow:'0 10px 25px -5px rgba(0,0,0,0.15)' }}>
        <Box ref={searchRef} sx={{ p:1.5, borderBottom:`1px solid ${border}` }}>
          <TextField fullWidth size="small" placeholder="Search..." value={searchValue} onChange={e => onSearchChange(e.target.value)} autoFocus
            InputProps={{ startAdornment:<InputAdornment position="start"><SearchIcon sx={{fontSize:18,color:textSecondary}}/></InputAdornment>, endAdornment: searchValue && <IconButton size="small" onClick={()=>onSearchChange('')}><ClearIcon sx={{fontSize:16}}/></IconButton> }}
            sx={{ '& .MuiOutlinedInput-root':{ borderRadius:'8px', bgcolor:inputBg, '& input':{color:textPrimary} } }}
          />
        </Box>
        <Box sx={{ px:1.5, py:1, borderBottom:`1px solid ${border}` }}>
          <Button size="small" onClick={()=>onSelectAll(options)} sx={{ textTransform:'none', color:accent, fontSize:12, fontWeight:500 }}>
            {selected.length===options.length ? 'Deselect All' : 'Select All'}
          </Button>
        </Box>
        <List ref={listRef} onScroll={()=>{ if(listRef.current) scrollTopRef.current=listRef.current.scrollTop; }} sx={{ maxHeight:260, overflow:'auto', p:0 }}>
          {filtered.length > 0 ? filtered.map(opt => {
            const sel = selected.some(s => getId(s)===getId(opt));
            return (
              <ListItem key={getId(opt)} onClick={()=>onToggle(opt)} sx={{ cursor:'pointer', py:1.5, '&:hover':{bgcolor: mode==='dark' ? 'rgba(255,255,255,0.05)' : '#f5f5f5'} }}>
                <ListItemIcon sx={{minWidth:36}}><Checkbox checked={sel} icon={icon} checkedIcon={checkedIcon} sx={{p:0,color:textSecondary,'&.Mui-checked':{color:accent}}}/></ListItemIcon>
                <ListItemText primary={getName(opt)} primaryTypographyProps={{fontSize:13,fontWeight:sel?500:400,color:textPrimary}}/>
              </ListItem>
            );
          }) : (
            <ListItem disabled sx={{justifyContent:'center',py:3}}>
              <Typography variant="body2" color={textSecondary}>No options found</Typography>
            </ListItem>
          )}
        </List>
      </Paper>
    </ClickAwayListener>
  );
};

/* ── CustomDatePicker ────────────────────────────────────────────── */
const CustomDatePicker = ({ label, value, onChange, color='primary', ...props }) => {
  const theme = useTheme();
  const mode  = theme.palette.mode;
  const getColor = () => color==='warning' ? (mode==='dark' ? '#ff9800' : '#F59E0B') : (mode==='dark' ? '#90caf9' : '#0D47A1');

  return (
    <DatePicker label={label} value={value} onChange={onChange} views={['year','month','day']}
      slotProps={{
        textField: { fullWidth:true, size:'small', sx:{ bgcolor: mode==='dark' ? '#2a2a42' : '#f9fafb', borderRadius:'10px', '& .MuiInputLabel-root':{color: mode==='dark' ? '#888' : '#6b7280', fontSize:'13px', '&.Mui-focused':{color:getColor()}}, '& .MuiOutlinedInput-root':{borderRadius:'10px','& fieldset':{borderColor: mode==='dark' ? '#333' : '#e5e7eb'},'&:hover fieldset':{borderColor:getColor()},'&.Mui-focused fieldset':{borderColor:getColor(),borderWidth:'1px'}} } },
        popper: { sx:{ zIndex:'9999999 !important', '& .MuiPaper-root':{zIndex:'9999999 !important',borderRadius:'12px',boxShadow:'0 25px 50px -12px rgba(0,0,0,0.25)'} }, disablePortal:false, placement:'bottom-start', modifiers:[{name:'preventOverflow',enabled:true,options:{boundary:'viewport',altBoundary:true,padding:8}},{name:'flip',enabled:true,options:{fallbackPlacements:['bottom-start','bottom-end','top-start','top-end']}}] },
      }}
      {...props}
    />
  );
};

/* ══ Main ════════════════════════════════════════════════════════════ */
const QuickFilterModalForMarketer = ({ open, onClose, onApplyFilters }) => {
  const theme         = useTheme();
  const mode          = theme.palette.mode;
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch      = useDispatch();

  const bg          = mode==='dark' ? '#1a1a2e' : '#ffffff';
  const textPrimary = mode==='dark' ? '#ffffff' : '#1a1a2e';
  const textSec     = mode==='dark' ? '#888888' : '#6b7280';
  const border      = mode==='dark' ? '#333333' : '#e5e7eb';
  const inputBg     = mode==='dark' ? '#2a2a42' : '#f9fafb';
  const accent      = '#0D47A1';

  const [filters, setFilters] = useState(EMPTY_FILTERS);

  // ✅ Last Filter
  const [lastFilter, setLastFilter] = useState(() => {
    try { const s = localStorage.getItem(LAST_FILTER_KEY); return s ? JSON.parse(s) : null; }
    catch { return null; }
  });

  const [openMenu,       setOpenMenu]       = useState(null);
  const [searchValue,    setSearchValue]    = useState('');
  const [anchorPosition, setAnchorPosition] = useState({ top:0, left:0, width:0 });

  const fieldRefs = {
    stage: useRef(null), oldstage: useRef(null), sales: useRef(null),
    chanel: useRef(null), campaign: useRef(null), project: useRef(null),
    developer: useRef(null), assignedTo: useRef(null),
  };

  const ressales     = useSelector(s => s.AllSales.sales);
  const reschannel   = useSelector(s => s.AllChannalRoot.channal);
  const resproject   = useSelector(s => s.AllProjects.Projects);
  const resstages    = useSelector(s => s.Allstages.stage);
  const resdevelopers= useSelector(s => s.AllDevelopers.Developers);
  const rescampaigns = useSelector(s => s.AllCampaign.campaign);

  const getId   = useCallback(item => item?._id||item?.id||item?.name||item?.CampainName||JSON.stringify(item), []);
  const getName = useCallback(item => item?.name||item?.CampainName||item?.title||'Unknown', []);

  const salesarray      = useMemo(() => (Array.isArray(ressales?.data) ? ressales.data : []).filter(s => !EXCLUDE.includes(s?.name)).sort((a,b) => a?.name?.localeCompare(b?.name)), [ressales]);
  const stagesarray     = Array.isArray(resstages?.data)     ? resstages.data     : [];
  const channelarray    = Array.isArray(reschannel?.data)    ? reschannel.data    : [];
  const projectarray    = Array.isArray(resproject?.data)    ? resproject.data    : [];
  const developersarray = Array.isArray(resdevelopers?.data) ? resdevelopers.data : [];
  const campaignsarray  = Array.isArray(rescampaigns?.data)  ? rescampaigns.data  : [];

  const uniqueStageNames = useMemo(() => [...new Set(stagesarray.map(s => getName(s)))], [stagesarray, getName]);

  useEffect(() => {
    if (open) {
      dispatch(Get_All_Sales_Action()); dispatch(GetAllchannalData());
      dispatch(GetallProjectsdata()); dispatch(GetAll_stage_Data());
      dispatch(GetDevelopersData()); dispatch(GetAllCampainData());
    }
  }, [open, dispatch]);

  const handleToggle = useCallback((filterType, item) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: prev[filterType].some(i => getId(i)===getId(item))
        ? prev[filterType].filter(i => getId(i)!==getId(item))
        : [...prev[filterType], item],
    }));
  }, [getId]);

  const handleSelectAll = useCallback((filterType, options) => {
    setFilters(prev => ({ ...prev, [filterType]: prev[filterType].length===options.length ? [] : [...options] }));
  }, []);

  const handleOpenMenu = (menuType) => {
    const ref = fieldRefs[menuType];
    if (ref?.current) {
      const rect = ref.current.getBoundingClientRect();
      setAnchorPosition({ top:rect.bottom+window.scrollY+4, left:rect.left+window.scrollX, width:rect.width });
      setOpenMenu(menuType); setSearchValue('');
    }
  };

  const handleCloseMenu = () => { setOpenMenu(null); setSearchValue(''); };

  const handleDateChange = (type, field, value) => {
    setFilters(prev => ({ ...prev, [type]: { ...prev[type], [field]: value ? moment(value).format('YYYY-MM-DD') : null } }));
  };

  // ✅ Restore last filter
  const handleRestoreLastFilter = () => { if (lastFilter) setFilters(lastFilter); };

  const handleApply = () => {
    const cleaned = {
      ...filters,
      stage: filters.stage.filter(Boolean), oldstage: filters.oldstage.filter(Boolean),
      sales: filters.sales.filter(Boolean), chanel: filters.chanel.filter(Boolean),
      campaign: filters.campaign.filter(Boolean), project: filters.project.filter(Boolean),
      developer: filters.developer.filter(Boolean), assignedTo: filters.assignedTo.filter(Boolean),
    };

    const q = new URLSearchParams();
    ['stage','sales','chanel','campaign','project','developer'].forEach(f => { cleaned[f]?.forEach(item => q.append(f, getId(item))); });
    if (cleaned.oldstage?.length)   cleaned.oldstage.forEach(i   => q.append('oldstage',   getId(i)));
    if (cleaned.assignedTo?.length) cleaned.assignedTo.forEach(i => q.append('assignedTo', getId(i)));
    if (cleaned.budget)                    q.append('budget',        cleaned.budget);
    if (cleaned.createdAt.from)            q.append('createdFrom',   cleaned.createdAt.from);
    if (cleaned.createdAt.to)              q.append('createdTo',     cleaned.createdAt.to);
    if (cleaned.stagedateupdated.from)     q.append('stageDateFrom', cleaned.stagedateupdated.from);
    if (cleaned.stagedateupdated.to)       q.append('stageDateTo',   cleaned.stagedateupdated.to);

    // ✅ حفظ الـ filter الحالي
    try { localStorage.setItem(LAST_FILTER_KEY, JSON.stringify(filters)); setLastFilter(filters); }
    catch(e) { console.error('Failed to save last filter:', e); }

    onApplyFilters(cleaned, q.toString());
    handleCloseMenu();
    onClose();
  };

  const handleClear = () => { setFilters(EMPTY_FILTERS); handleCloseMenu(); };

  const getOptions = (menuType) => {
    switch(menuType) {
      case 'stage': case 'oldstage': return stagesarray;
      case 'sales': case 'assignedTo': return salesarray;
      case 'chanel': return channelarray;
      case 'campaign': return campaignsarray;
      case 'project': return projectarray;
      case 'developer': return developersarray;
      default: return [];
    }
  };

  const FilterField = ({ title, filterType, icon: Icon, placeholder }) => {
    const selected = filters[filterType] || [];
    const isOpen   = openMenu === filterType;
    return (
      <Box sx={{ mb:2.5 }}>
        <Typography sx={{ fontSize:12, fontWeight:500, color:textSec, mb:1, display:'flex', alignItems:'center', gap:0.5 }}>
          {Icon && <Icon sx={{fontSize:14,color:accent}}/>} {title}
        </Typography>
        <Box ref={fieldRefs[filterType]} onClick={() => isOpen ? handleCloseMenu() : handleOpenMenu(filterType)}
          sx={{ border:`1px solid ${isOpen ? accent : border}`, borderRadius:'10px', bgcolor:inputBg, px:2, py:1.5, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'space-between', transition:'border-color 0.15s', '&:hover':{borderColor:accent} }}>
          <Box sx={{ display:'flex', flexWrap:'wrap', gap:0.5, flex:1 }}>
            {selected.length > 0 ? (
              <>
                {selected.slice(0,2).map((item,idx) => (
                  <Chip key={idx} label={getName(item)} size="small" onDelete={e => { e.stopPropagation(); handleToggle(filterType, item); }}
                    sx={{ height:24, fontSize:11, borderRadius:'6px', bgcolor:accent, color:'#fff', '& .MuiChip-deleteIcon':{color:'#fff',fontSize:14} }} />
                ))}
                {selected.length > 2 && <Chip label={`+${selected.length-2}`} size="small" sx={{height:24,fontSize:11,bgcolor:border,color:textSec}}/>}
              </>
            ) : (
              <Typography sx={{fontSize:13,color:textSec}}>{placeholder}</Typography>
            )}
          </Box>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{transform: isOpen ? 'rotate(180deg)' : 'none', transition:'transform 0.2s', flexShrink:0, marginLeft:8}}>
            <path d="M6 9L12 15L18 9" stroke={textSec} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </Box>
      </Box>
    );
  };

  const StageCard = ({ stageName }) => {
    const stageObj   = stagesarray.find(s => getName(s)===stageName);
    const isSelected = stageObj && filters.stage.some(s => getId(s)===getId(stageObj));
    const color      = stageColors[stageName] || accent;
    return (
      <Box onClick={() => stageObj && handleToggle('stage', stageObj)}
        sx={{ px:2, py:1, borderRadius:'20px', border:`1px solid ${border}`, bgcolor: isSelected ? color : 'transparent', cursor:'pointer', transition:'all 0.15s', textAlign:'center',
          '&:hover':{ transform:'translateY(-1px)', boxShadow:'0 2px 8px rgba(0,0,0,0.05)', bgcolor: isSelected ? color : mode==='dark' ? 'rgba(255,255,255,0.05)' : '#f5f5f5' } }}>
        <Typography sx={{fontSize:12, fontWeight:500, color: isSelected ? '#fff' : textPrimary}}>{stageName}</Typography>
      </Box>
    );
  };

  const hasFilters = filters.budget || filters.stage.length>0 || filters.oldstage.length>0 ||
    filters.sales.length>0 || filters.chanel.length>0 || filters.campaign.length>0 ||
    filters.project.length>0 || filters.developer.length>0 || filters.assignedTo.length>0 ||
    filters.createdAt.from || filters.createdAt.to || filters.stagedateupdated.from || filters.stagedateupdated.to;

  const currentOptions  = openMenu ? getOptions(openMenu) : [];
  const currentSelected = openMenu ? (filters[openMenu] || []) : [];

  return (
    <>
      <GlobalStyles styles={{ '.MuiPickersPopper-root':{zIndex:999999}, '.MuiPickersPopper-paper':{zIndex:999999} }} />

      <Dialog open={open} onClose={() => { handleCloseMenu(); onClose(); }}
        fullScreen={isSmallScreen} maxWidth="sm" fullWidth
        TransitionComponent={SlideTransition} disableEnforceFocus disableAutoFocus
        PaperProps={{ sx:{ borderRadius: isSmallScreen ? 0 : '20px', bgcolor:bg, margin: isSmallScreen ? 0 : 2, height: isSmallScreen ? '100vh' : 'auto', maxHeight:'90vh', overflow:'hidden', boxShadow:'0 25px 50px -12px rgba(0,0,0,0.25)' } }}
        sx={{ zIndex:50000 }}
      >
        {/* Header */}
        <DialogTitle sx={{ px:3, py:2.5, borderBottom:`1px solid ${border}`, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <Box sx={{ display:'flex', alignItems:'center', gap:1.5 }}>
            <FilterListIcon sx={{ color:accent, fontSize:22 }} />
            <Typography sx={{ fontSize:20, fontWeight:700, color:textPrimary, letterSpacing:'-0.01em' }}>Quick Filter</Typography>
          </Box>
          <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
            {/* ✅ زرار Last Filter */}
            {lastFilter && (
              <Button onClick={handleRestoreLastFilter} size="small" startIcon={<RestoreIcon sx={{fontSize:16}}/>}
                sx={{ textTransform:'none', fontSize:12, fontWeight:600, color:accent, bgcolor: mode==='dark' ? 'rgba(13,71,161,0.15)' : 'rgba(13,71,161,0.08)', borderRadius:'8px', px:1.5, py:0.5, '&:hover':{ bgcolor: mode==='dark' ? 'rgba(13,71,161,0.25)' : 'rgba(13,71,161,0.15)' } }}>
                Last Filter
              </Button>
            )}
            <IconButton onClick={() => { handleCloseMenu(); onClose(); }} size="small">
              <CloseIcon sx={{color:textSec}}/>
            </IconButton>
          </Box>
        </DialogTitle>

        {/* Body */}
        <DialogContent sx={{ p:3, overflow:'auto' }}>
          <LocalizationProvider dateAdapter={AdapterMoment}>

            {/* Budget */}
            <Box sx={{ mb:3 }}>
              <Typography sx={{fontSize:12, fontWeight:500, color:textSec, mb:1}}>Budget</Typography>
              <Box sx={{ border:`1px solid ${border}`, borderRadius:'10px', bgcolor:inputBg, p:'10px 12px' }}>
                <Box sx={{ display:'flex', alignItems:'center', gap:0.5 }}>
                  <Typography sx={{fontWeight:500,color:textSec}}>$</Typography>
                  <input type="text" placeholder="Enter budget amount..." value={filters.budget} onChange={e => setFilters(prev => ({...prev, budget: e.target.value}))}
                    style={{ border:'none', background:'transparent', outline:'none', fontSize:14, color:textPrimary, width:'100%', fontFamily:'inherit' }} />
                  {filters.budget && <ClearIcon sx={{fontSize:16,color:textSec,cursor:'pointer'}} onClick={() => setFilters(prev => ({...prev, budget: ''}))}/>}
                </Box>
              </Box>
            </Box>

            {/* Stage Cards */}
            <Box sx={{ mb:3 }}>
              <Typography sx={{fontSize:12, fontWeight:500, color:textSec, mb:1}}>Lead Stage</Typography>
              {stagesarray.length > 0 ? (
                <Box sx={{ display:'flex', flexWrap:'wrap', gap:1 }}>
                  {uniqueStageNames.map(name => <StageCard key={name} stageName={name}/>)}
                </Box>
              ) : (
                <Box sx={{ border:`1px solid ${border}`, borderRadius:'10px', p:2, textAlign:'center', bgcolor:inputBg }}>
                  <Typography sx={{fontSize:13,color:textSec}}>Loading stages...</Typography>
                </Box>
              )}
            </Box>

            <FilterField title="Old Stage"    filterType="oldstage"   icon={HistoryIcon}           placeholder="Select Old Stage..." />
            <FilterField title="Sales Agents" filterType="sales"                                   placeholder="Search by name..." />
            <FilterField title="Channel"      filterType="chanel"                                  placeholder="Select Channel..." />
            <FilterField title="Campaign"     filterType="campaign"                                placeholder="Select Campaign..." />
            <FilterField title="Project"      filterType="project"                                 placeholder="Select Project..." />
            <FilterField title="Developer"    filterType="developer"                               placeholder="Select Developer..." />
            <FilterField title="Assigned To"  filterType="assignedTo" icon={AssignmentTurnedInIcon} placeholder="Select Assigned To Sales..." />

            {/* Creation Date */}
            <Box sx={{ mb:2.5 }}>
              <Typography sx={{fontSize:12,fontWeight:500,color:textSec,mb:1}}>Creation Date Range</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}><CustomDatePicker label="From Date" value={filters.createdAt.from ? moment(filters.createdAt.from) : null} onChange={d => handleDateChange('createdAt','from',d)}/></Grid>
                <Grid item xs={6}><CustomDatePicker label="To Date"   value={filters.createdAt.to   ? moment(filters.createdAt.to)   : null} onChange={d => handleDateChange('createdAt','to',d)}/></Grid>
              </Grid>
            </Box>

            {/* Stage Date */}
            <Box sx={{ mb:2.5 }}>
              <Box sx={{ display:'flex', alignItems:'center', gap:1, mb:1 }}>
                <UpdateIcon sx={{fontSize:16,color:'#F59E0B'}}/>
                <Typography sx={{fontSize:12,fontWeight:500,color:textSec}}>Stage Date Range</Typography>
              </Box>
              <Typography variant="caption" sx={{color:textSec,fontSize:10,mb:1,display:'block'}}>Filter by stage update date</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}><CustomDatePicker label="From Date" value={filters.stagedateupdated.from ? moment(filters.stagedateupdated.from) : null} onChange={d => handleDateChange('stagedateupdated','from',d)} color="warning"/></Grid>
                <Grid item xs={6}><CustomDatePicker label="To Date"   value={filters.stagedateupdated.to   ? moment(filters.stagedateupdated.to)   : null} onChange={d => handleDateChange('stagedateupdated','to',d)}   color="warning"/></Grid>
              </Grid>
            </Box>

            {/* Summary */}
            {hasFilters && (
              <Box sx={{ mt:3, p:2, bgcolor: mode==='dark' ? 'rgba(13,71,161,0.1)' : 'rgba(13,71,161,0.05)', borderRadius:2 }}>
                <Typography variant="subtitle2" sx={{fontWeight:'bold',color:textPrimary,mb:1,fontSize:12}}>Selected Filters:</Typography>
                <Box sx={{ display:'flex', flexWrap:'wrap', gap:1 }}>
                  {filters.budget && <Chip label={`Budget: $${filters.budget}`} size="small" onDelete={() => setFilters(p => ({...p,budget:''}))} sx={{height:24,fontSize:11}}/>}
                  {filters.stage.length>0      && <Chip label={`Stage: ${filters.stage.length}`}           size="small" sx={{height:24,fontSize:11}}/>}
                  {filters.oldstage.length>0   && <Chip label={`Old Stage: ${filters.oldstage.length}`}    size="small" icon={<HistoryIcon/>} sx={{height:24,fontSize:11}}/>}
                  {filters.sales.length>0      && <Chip label={`Sales: ${filters.sales.length}`}           size="small" sx={{height:24,fontSize:11}}/>}
                  {filters.chanel.length>0     && <Chip label={`Channel: ${filters.chanel.length}`}        size="small" sx={{height:24,fontSize:11}}/>}
                  {filters.campaign.length>0   && <Chip label={`Campaign: ${filters.campaign.length}`}     size="small" sx={{height:24,fontSize:11}}/>}
                  {filters.project.length>0    && <Chip label={`Project: ${filters.project.length}`}       size="small" sx={{height:24,fontSize:11}}/>}
                  {filters.developer.length>0  && <Chip label={`Developer: ${filters.developer.length}`}   size="small" sx={{height:24,fontSize:11}}/>}
                  {filters.assignedTo.length>0 && <Chip label={`Assigned To: ${filters.assignedTo.length}`} size="small" icon={<AssignmentTurnedInIcon/>} sx={{height:24,fontSize:11}}/>}
                  {(filters.createdAt.from||filters.createdAt.to) && <Chip label="Creation Date Range" size="small" sx={{height:24,fontSize:11}}/>}
                  {(filters.stagedateupdated.from||filters.stagedateupdated.to) && <Chip label="Stage Date Range" icon={<UpdateIcon/>} size="small" sx={{height:24,fontSize:11}}/>}
                </Box>
              </Box>
            )}

          </LocalizationProvider>
        </DialogContent>

        {/* Footer */}
        <DialogActions sx={{ px:3, py:2.5, borderTop:`1px solid ${border}`, gap:2 }}>
          <Button onClick={handleClear} sx={{color:textSec,textTransform:'none',fontWeight:500,fontSize:13,'&:hover':{bgcolor:'transparent',color:accent}}}>CLEAR ALL</Button>
          <Box sx={{flex:1}}/>
          <Button onClick={() => { handleCloseMenu(); onClose(); }} sx={{color:textSec,textTransform:'none',fontWeight:500,fontSize:13,'&:hover':{bgcolor:'transparent',color:accent}}}>Cancel</Button>
          <Button onClick={handleApply} variant="contained" sx={{bgcolor:accent,textTransform:'none',fontWeight:600,px:4,borderRadius:'10px',fontSize:13,boxShadow:'none','&:hover':{bgcolor:'#0D47A1',boxShadow:'none'}}}>Apply Filters</Button>
        </DialogActions>
      </Dialog>

      <StableMenu
        open={!!openMenu} options={currentOptions} selected={currentSelected}
        searchValue={searchValue} onSearchChange={setSearchValue}
        onToggle={item => handleToggle(openMenu, item)}
        onSelectAll={opts => handleSelectAll(openMenu, opts)}
        onClose={handleCloseMenu} anchorPosition={anchorPosition}
        mode={mode} textSecondary={textSec} inputBg={inputBg} bg={bg} border={border} accent={accent}
        getId={getId} getName={getName}
      />
    </>
  );
};

export default QuickFilterModalForMarketer;
